import { create } from 'zustand';
import type { TermsDocType } from '@/lib/api';

/**
 * On-demand terms-acceptance prompt. The blocking TermsGate handles the
 * docs that ALREADY apply to a user on sign-in (driver_tos always, plus
 * payout_terms / sp_terms once they are an earner). But a brand-new earner
 * flipping on their first earning surface isn't flagged as an earner yet, so
 * the server gates that enable call with 409 PAYOUT_TERMS_REQUIRED. The api
 * wrappers catch that, call `requestTermsAcceptance('payout_terms')` to raise
 * this prompt, and retry the enable once the user accepts.
 *
 * A single <TermsPromptHost/> (mounted in AppShell) renders the accept card
 * whenever `pending` is set and resolves the promise the caller is awaiting.
 */
interface TermsPromptState {
  /** The doc type currently being prompted for, or null when idle. */
  pending: TermsDocType | null;
  /** Resolves the promise returned by requestTermsAcceptance. */
  resolve: ((accepted: boolean) => void) | null;
  /** Show the accept card for `docType`; resolves true if accepted, false if
   *  dismissed. If a prompt is already open, the existing one wins and the new
   *  request resolves false (callers just skip their retry). */
  request: (docType: TermsDocType) => Promise<boolean>;
  /** Called by the host when the user accepts or dismisses. */
  settle: (accepted: boolean) => void;
}

export const useTermsPromptStore = create<TermsPromptState>((set, get) => ({
  pending: null,
  resolve: null,
  request: (docType) => {
    if (get().pending) return Promise.resolve(false);
    return new Promise<boolean>((resolve) => {
      set({ pending: docType, resolve });
    });
  },
  settle: (accepted) => {
    const { resolve } = get();
    resolve?.(accepted);
    set({ pending: null, resolve: null });
  },
}));

/** Imperative entry point for non-React callers (the api-layer 409 retry). */
export function requestTermsAcceptance(docType: TermsDocType): Promise<boolean> {
  return useTermsPromptStore.getState().request(docType);
}
